/* eslint jsx-a11y/anchor-is-valid: 0 */
/* eslint jsx-a11y/anchor-has-content: 0 */
/* eslint no-script-url: 0 */

import React from 'react';
import classnames from 'classnames';

import { i18n } from 'config/locales';

import ValidationElement, { newGuid } from 'components/Form/ValidationElement';
import SvgComponent from 'components/Form/SvgComponent';
import Textarea from 'components/Form/Textarea';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import DebugRender from 'components/Debug/Render/DebugRender';
import { FeatureFlag } from 'components/FeatureFlags/FeatureFlag';
import { withContext } from 'components/withContext/WithContext';
import { SubsectionContext } from 'components/Section/shared/SubsectionConnector';
import { SubsectionContextType } from 'components/Section/shared/types';
import { rootSelectors } from '@redux/modules/selectors';
import { RootState } from 'types/state';
import { connect } from 'react-redux';
import { addTestClassName } from 'helpers/testHelper';
import { DataAttributeType } from 'helpers/attributes/types';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import LinkButton from '../LinkButton';
import { ConnectedFieldErrors } from './components/FieldErrors';
import { FieldError } from './components/FieldError';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

export const renderMessage = (id: string, messageString?: string, titleString?: string) => {
  const noteId = `${id}.note`;
  let note = i18n.m(noteId);
  if (Object.prototype.toString.call(note) === '[object String]' && note.indexOf(noteId) > -1) {
    note = '';
  } else {
    note = (
      <LinkButton
        classContent="help-link"
      >
        <em>{note}</em>
      </LinkButton>
    );
  }

  const messageId = `${id}.message`;
  let message = messageString || i18n.m(messageId);
  if (
    Object.prototype.toString.call(message) === '[object String]'
    && message.indexOf(messageId) > -1
  ) {
    message = '';
  } else {
    message = (
      <LinkButton
        classContent="help-link"
      >
        <span>{message}</span>
      </LinkButton>
    );
  }

  const title = titleString || i18n.t(`${id}.title`);

  return (
    <div key={newGuid()} data-i18n={id} className={addTestClassName('render-message')}>
      <h5 className="usa-alert-heading">{title}</h5>
      {message}
      {note}
    </div>
  );
};

// XXX All references to `comments` in this component refer to help text / info boxes

interface OwnProps extends ValidationElementProps {
  errors?: ErrorCode[]
  errorPrefix?: string
  helpActive?: boolean
  comments?: boolean
  commentsActive?: any
  commentsValue?: any
  commentsName?: string
  commentsRemove?: string
  commentsAdd?: string
  commentsRequired?: boolean
  commentAttribute?: string
  filterErrors?: (errors: ErrorCode[]) => ErrorCode[]
  title?: string
  titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label'
  optional?: boolean
  optionalText?: string
  validate?: boolean
  labelFor?: any
  /** This is the locale id. */
  help?: string
  /** Overrides the help locale id */
  helpMessage?: any
  /** Overrides the help locale */
  helpTitle?: any
  adjustFor?: any
  onUpdate?: (a: any) => void
  onError?: () => void
  className?: any
  shrink?: boolean
  dataTestId?: string
  scrollIntoView?: boolean
  children?: React.ReactNode
  hideTitle?: boolean

  /**
   * When the field is pulling in non-related errors, you can change the
   * attribute matching to be a strict equal. However, by default, this will be
   * set to undefined (which defaults to false).
   */
  exactAttribute?: boolean

  /**
   * This is to be used with useStateFormErrors and subsectionKey. This allows
   * further filtering of the errors.
   */
  attribute?: DataAttributeType
}

interface State {
  uuid: string
  errors: ErrorCode[]
  helpActive: any
  isCommentActive: boolean
}

interface ContextProps {
  subsection?: SubsectionContextType
}

type Props = OwnProps & ContextProps & ReturnType<typeof mapStateToProps> // eslint-disable-line no-use-before-define

class Field extends ValidationElement<Props, State> {
  static defaultProps: Partial<Props> = {
    title: '',
    titleSize: 'h4',
    className: '',
    errors: [],
    errorPrefix: '',
    help: '',
    helpActive: false,
    adjustFor: '',
    comments: false,
    commentsName: 'Comments',
    commentsValue: {},
    commentsActive: false,
    commentsAdd: 'comments.add',
    commentsRemove: 'comments.remove',
    commentsRequired: false,
    optional: false,
    optionalText: '',
    validate: true,
    shrink: false,
    scrollIntoView: true,
    dataTestId: '',
    filterErrors: (errors) => errors,
    onUpdate: () => undefined,
    hideTitle: false,
  };

  errorMessagesRef: any;

  helpMessageRef: any;

  commentsRef: any;

  errors: ErrorCode[];

  field: any;

  constructor(props: Props) {
    super(props);

    this.state = {
      uuid: `field-${super.guid()}`,
      errors: props.errors ?? [],
      helpActive: props.helpActive,
      isCommentActive: !!props.commentsValue?.value,
    };

    this.errors = props.errors || [];

    this.field = React.createRef();
    this.helpMessageRef = React.createRef();
    this.errorMessagesRef = React.createRef();
    this.commentsRef = React.createRef();
  }

  /**
   * Handle the click event for the rendering of messages.
   */
  toggleHelp = () => {
    const { helpActive } = this.state;

    this.setState({ helpActive: !helpActive }, () => {
      this.scrollIntoView(this.helpMessageRef.current);
    });
  };

  /**
   * Toggle the comment visibility.
   */
  toggleComments = () => {
    this.setState(
      (prevState) => ({
        isCommentActive: !prevState.isCommentActive,
      }),
      () => {
        if (!this.state.isCommentActive) {
          this.clearComment();
        }
      }
    );
  };

  removeComments = () => {
    this.setState(
      {
        isCommentActive: false,
      },
      () => {
        this.clearComment();
      }
    );
  };

  clearComment = () => {
    if (this.commentsRef?.current?.clearValue) {
      this.commentsRef.current.clearValue(true);
    }
  };

  /**
   * Determines if the comments should be visible.
   */
  visibleComments = () => {
    const { comments, commentsActive } = this.props;
    const { isCommentActive } = this.state;

    return comments && (isCommentActive || commentsActive);
  };

  handleError = (value: any, arr: ErrorCode[] = []) => {
    if (this.props.EN4733 === true) { return null; }

    const errors = [...this.errors];

    if (arr.length === 0) {
      if (errors.length && errors.some((err) => err.valid === false)) {
        this.scrollIntoView(this.errorMessagesRef.current);
      }
      return arr;
    }

    arr.forEach((e) => {
      const idx = errors.findIndex((x) => x.uid === e.uid && x.code === e.code);
      if (idx !== -1) {
        errors[idx] = { ...e };
      } else {
        errors.push({ ...e });
      }
    });

    // Store in instance variable to update immediately as opposed to storing in state
    // which is an asynchronous operation. This prevents the issue where one call
    // overrides the errors of another call if both are executed almost at the time same.
    this.errors = [...errors];
    this.setState({ errors }, () => {
      if (errors.length && errors.some((err) => err.valid === false)) {
        this.scrollIntoView(this.errorMessagesRef.current);
      }
    });

    return arr;
  };

  /**
   * Render the title as needed
   */
  title = (required = false) => {
    const {
      title, titleSize, optionalText, labelFor, hideTitle
    } = this.props;

    if (title) {
      const titleClasses = classnames('title', titleSize, { 'hide-title': hideTitle });

      const optional = !required && optionalText ? <span className="optional">{optionalText}</span> : null;

      // Apply the semantic element for accessibility
      switch (titleSize) {
        case 'h1':
          return (
            <h1 className={titleClasses}>
              {title}
              {optional}
            </h1>
          );
        case 'h2':
          return (
            <h2 className={titleClasses}>
              {title}
              {optional}
            </h2>
          );
        case 'h3':
          return (
            <h3 className={titleClasses}>
              {title}
              {optional}
            </h3>
          );
        case 'h4':
          return (
            <h4 className={titleClasses}>
              {title}
              {optional}
            </h4>
          );
        case 'h5':
          return (
            <h5 className={titleClasses}>
              {title}
              {optional}
            </h5>
          );
        case 'h6':
          return (
            <h6 className={titleClasses}>
              {title}
              {optional}
            </h6>
          );
        case 'label':
          return (
            <label className={titleClasses} htmlFor={labelFor}>
              {title}
              {optional}
            </label>
          );

        default:
          return (
            <span className={titleClasses}>
              {title}
              {optional}
            </span>
          );
      }
    }

    return null;
  };

  /**
   * Render the comments toggle link if needed.
   */
  commentsButton = () => {
    const { comments, commentsRemove, commentsAdd } = this.props;
    if (!comments) {
      return null;
    }

    if (this.visibleComments()) {
      return (
        <LinkButton
          classContent="comments-button remove"
          onClick={this.removeComments}
        >
          <span>{i18n.t(commentsRemove)}</span>
          <div className="cross-circle" aria-hidden="true">
            <SvgComponent className="cross-circle" src="/img/cross-circle.svg" />
          </div>
        </LinkButton>
      );
    }

    return (
      <LinkButton
        classContent="comments-button add"
        onClick={this.toggleComments}
      >
        <span>{i18n.t(commentsAdd)}</span>
        <div className="cross-circle" aria-hidden="true">
          <SvgComponent className="cross-circle" src="/img/plus-circle.svg" />
        </div>
      </LinkButton>
    );
  };

  /**
   * Render the comments if necessary.
   */
  comments = () => {
    const {
      comments,
      commentsName,
      commentsValue,
      onError,
      onUpdate,
      onValidate,
      commentsRequired,
    } = this.props;
    if (!comments || !this.visibleComments()) {
      return null;
    }

    return (
      <Textarea
        ref={this.commentsRef}
        name={commentsName}
        attribute={this.props.commentAttribute}
        value={commentsValue.value}
        className="comments"
        onError={onError}
        onUpdate={onUpdate}
        onValidate={onValidate}
        required={commentsRequired}
      />
    );
  };

  /**
   * Render the help icon if needed.
   */
  icon = () => {
    const {
      help, title, titleSize, adjustFor
    } = this.props;
    const { helpActive } = this.state;

    if (!help) {
      return null;
    }

    const iconClasses = classnames('toggle', titleSize, {
      active: helpActive,
      [`adjust-for-${adjustFor}`]: !!adjustFor,
    });

    const titleString = `Show help${title && ' for '}${title || ''}`.trim();

    return (
      <LinkButton
        title={titleString}
        ariaLabel={titleString}
        classContent={iconClasses}
        ignoreLinkStyle
        onClick={() => this.toggleHelp()}
      >

        <SvgComponent src="/img/info.svg" />
      </LinkButton>
    );
  };

  /**
   * Render the help messages allowing for Markdown syntax.
   */
  helpMessage = () => {
    const { help, helpMessage, helpTitle } = this.props;

    if (this.state.helpActive && help) {
      return (
        <div className="usa-alert usa-alert-info" role="alert">
          <div className="usa-alert-body">
            {renderMessage(help, helpMessage, helpTitle)}
            <LinkButton
              classContent="close"
              title={i18n.t('help.close')}
              onClick={() => this.toggleHelp()}
            >
              {i18n.t('help.close')}
            </LinkButton>
          </div>
        </div>
      );
    }

    return null;
  };

  /**
   * Render the error messages allowing for Markdown syntax.
   */
  errorMessages = () => {
    const el = [];
    const stateErrors = this.props.filterErrors?.(this.errors || []) ?? this.errors;
    let errors = stateErrors.filter(
      (err) => err.valid === false
        && !err.code.includes('required')
        && !err.code.includes('country.notfound')
    );

    const required = stateErrors
      .filter((err) => err.code.includes('required') && err.valid === false)
      .sort((e1, e2) => e1.code.split('.').length - e2.code.split('.').length);

    if (required.length) {
      errors = errors.concat(required[0]);
    }

    if (errors.length) {
      const markup = errors.map((err) => renderMessage(`error.${err.code}`));
      const guid = super.guid();
      el.push(
        <FieldError guid={guid} key={guid}>
          {markup}
        </FieldError>
      );
    }

    return el;
  };

  /**
   * Iterate through the children and bind methods to them.
   */
  children = (el) => {
    if (this.props.EN4733 === true) { return el; }

    return React.Children.map(el, (child) => {
      const { props } = child || {};
      if (!child || !props) {
        return child;
      }

      // TODO: Clean up shallow copy; should use lodash cloneDeep to prevent possible mutations
      const extendedProps = { ...props };
      let injected = false;

      if (React.isValidElement(child) && props.onError) {
        // Inject ourselves in to the validation callback
        injected = true;
        extendedProps.onError = (value, arr) => this.handleError(value, props.onError(value, arr));
      }

      if (props.children && !injected) {
        const typeOfChildren = Object.prototype.toString.call(props.children);
        if (props.children && ['[object Object]', '[object Array]'].includes(typeOfChildren)) {
          extendedProps.children = this.children(props.children);
        }
      }

      return React.cloneElement(child, extendedProps);
    });
  };

  /**
   * Checks if the children and help message are within the current viewport. If not, scrolls the
   * help message into view so that users can see the message without having to manually scroll.
   */
  scrollIntoView = (ref) => {
    if (!ref) {
      return;
    }

    // Grab the bottom position for the help container
    const helpBottom = ref.getBoundingClientRect().bottom;

    // Grab the current window height
    const winHeight = window.innerHeight;

    // Flag if help container bottom is within current viewport
    const notInView = winHeight < helpBottom;

    const active = this.state.helpActive || this.errors.some((x) => x.valid === false);

    if (active && this.props.scrollIntoView && notInView) {
      window.scrollBy({
        top: helpBottom - winHeight,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  // * We can't call this within the render, as it'll detect a new function.
  // * We can't move this into a functional component as we need to set the
  //   props before passing it.
  renderConnectedFields = () => (
    <ConnectedFieldErrors
      exact={this.props.exactAttribute}
      attribute={this.props.attribute}
      subsectionKey={this.props.subsection?.subsectionKey}
      showRequiredErrors={this.props.subsection?.showRequiredErrors}
      displayErrorsReviewOnly={this.props.subsection?.displayErrorsReviewOnly || []}
    />
  );

  render() {
    const {
      optional, className, shrink, dataTestId
    } = this.props;
    const required = !optional;

    const classes = classnames(
      'field',
      {
        required,
        'with-comments': this.visibleComments(),
      },
      className
    );
    const componentClasses = classnames('component', {
      shrink,
    });

    /* eslint jsx-a11y/anchor-has-content: 0 */
    /* eslint jsx-a11y/anchor-is-valid: 0 */
    return (
      <div
        className={classes}
        data-uuid={this.state.uuid}
        ref={this.field}
        aria-label={this.props.title}
        {...(dataTestId && { 'data-test-id': dataTestId })}
      >
        <AttributeAnchors attribute={this.props.attribute} prefix="field" />
        <a id={this.state.uuid} name={this.state.uuid} className="anchor" aria-hidden="true" />
        {this.title(required)}
        <span className="icon">{this.icon()}</span>
        <DebugRender parent={this} />

        <div className="table expand">
          <span className="messages help-messages" ref={this.helpMessageRef} aria-live="polite">
            {this.helpMessage()}
          </span>
        </div>

        <div className="table expand">
          <span
            className="messages error-messages"
            ref={this.errorMessagesRef}
            role="alert"
            aria-live="polite"
          >
            <FeatureFlag
              flagId="EN-4733"
              when={this.props.subsection?.subsectionKey !== undefined}
              ActiveRender={this.renderConnectedFields}
              InactiveRender={this.errorMessages}
            />
            {this.props.isReviewBottomError === true && this.props.subsection?.showRequiredErrors === true
              && <div>This error message above applies to the section above. Please review all responses.</div>}
          </span>
        </div>

        <div className="table">
          <span className="content">
            <span className={componentClasses}>
              {this.children(this.props.children)}
              {this.comments()}
              {this.commentsButton()}
            </span>
          </span>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  // Once FF is implemented, this can be removed.
  EN4733: rootSelectors.featureFlags.isFeatureFlagActive(state, 'EN-4733'),
});

export default withContext(connect(mapStateToProps)(Field), SubsectionContext, 'subsection');
